import { getAdsContext } from "../../../helpers/adsContext/getAdsContext";
import { AdEnginePlugin, type Hooks } from "../../../core/AdEnginePlugin";
import { PrebidPlugin } from "../PrebidPlugin";
import { LiveConnectPlugin } from "../../LiveConnectPlugin/LiveConnectPlugin";
import type { AdEngine } from "../../../core/AdEngine";
import type { InstantConfigRepository } from "instant-config";
import { pushTrack } from "utils";
import { IdentityInfoEvent } from "common-types";

/**
 * The purpose of the PrebidIdSyncingPlugin is to generate a 16 bit string
 * that will be sent to GAM in the boi key. This string contains all the identity partners
 * that come back from prebid and that have been active, except one bit that's sourced
 * directly from localStorage or from the HashedEmail that's set by MediaWiki when a user is logged in.
 * */
export class PrebidIdSyncingPlugin extends AdEnginePlugin {
  private readonly YAHOO_BIT = 0;
  private readonly ID5_BIT = 1;
  private readonly HEM_BIT = 2;
  private readonly LIVEINTENT_BIT = 3;
  private readonly PUBCID_BIT = 4;
  private readonly UID2_BIT = 5;

  private readonly BIT_PLACEHOLDER = "x";
  private readonly ID_STRING_LENGTH = 16;

  private readonly PRESENT_PLACEHOLDER = "P";
  private readonly ABSENT_PLACEHOLDER = "A";

  private readonly LIVE_CONNECT_PLACEHOLDER = "L";
  private readonly MEDIA_WIKI_PLACEHOLDER = "M";

  private pbjs?: Prebid;

  constructor(
    private readonly config: Pick<
      InstantConfigRepository,
      "icLiveIntentConnectedId"
    >,
  ) {
    super();
  }

  /* TODO: Right now, all of the entries in this Record are called and evaluated
   *  each time requestBid fires on pbjs.
   *  Maybe there's a better way to do this, such as caching these values or storing them
   *  as member variables, when we know the values won't change anymore? */
  /**
   * @see {@link https://fandom.atlassian.net/wiki/spaces/TACO/pages/2800812083/Integrated+Partners}
   * for more information on partner integrations and the generation of the boi (bag of identifiers) GAM KVP.
   *
   * The values that are generated by all the bits except the {@link HEM_BIT} can be compared with the
   * output of pbjs.getUserIdsAsEids().
   *
   * The Professor Prebid Extension @see
   *   {@link https://chromewebstore.google.com/detail/professor-prebid/kdnllijdimhbledmfdbljampcdphcbdc} is also
   *   useful for a quick look. The `USER ID` tab shows the output pbjs.getUserIdsAsEids().
   * */
  private readonly ID_MAP: Record<number, (sources: PrebidEids[]) => string> = {
    /* 1st entry in the 16 bit string.
     * This can be forced with the icbm__icPrebidYahooConnectId=true query param.
     * If this is the only activate prebid partner,
     * the string would look like this: PAAAAAxxxxxxxxxx
     */
    [this.YAHOO_BIT]: (sources: PrebidEids[]) =>
      this.getDefaultBitStatus(sources, "yahoo.com"),
    /* 2nd entry in the 16 bit string.
     * This can be forced with the icbm__icPrebidId5 query param.
     *
     * If this is the only activate prebid partner,
     * the string would look like this: APAAAAAxxxxxxxxxx
     */
    [this.ID5_BIT]: (sources: PrebidEids[]) =>
      this.getDefaultBitStatus(sources, "id5-sync.com"),
    /* 3rd entry in the 16 bit string.
     * This entry can have three outcomes, with the following values if no other prebid partners are active:
     *  1) Could be forced with the icbm__icLiveConnect=true query param, but it is not guaranteed.
     *    AALAAAAxxxxxxxxxx
     *  2) Login on a Fandom wiki (MediaWiki). This will force the HEM bit to be M for MediaWiki.
     *    AAMAAAAxxxxxxxxxx
     *  3) If neither can be sourced, then the bit will be A for Absent.
     *    AAAAAAAxxxxxxxxxx */
    [this.HEM_BIT]: () => this.getHEMBitStatus(),
    /* 4th entry in the 16 bit string.
     * This can be forced with the icbm__icLiveIntentConnectedId=true query param.
     *
     * If this is the only activate prebid partner,
     * the string would look like this: AAAPAAxxxxxxxxxx
     */
    [this.LIVEINTENT_BIT]: (sources: PrebidEids[]) =>
      this.getDefaultBitStatus(sources, "liveintent.com"),
    /* 5th entry in the 16 bit string.
     * This can be forced with the icbm__icPrebidSharedId=true query param.
     *
     * If this is the only activate prebid partner,
     * the string would look like this: AAAAPAxxxxxxxxxx
     */
    [this.PUBCID_BIT]: (sources: PrebidEids[]) =>
      this.getDefaultBitStatus(sources, "pubcid.org"),
    /* 6th entry in the 16 bit string.
     * This can be forced with the icbm__icPrebidUID2=true query param.
     * The uid2 is sourced from the LiveConnect SHA2 HEM. This means that LiveConnect
     * had to have returned a SHA2 HEM (liveConnect key in localStorage) in order for
     * this value to be present.
     *
     * If this is the only activate prebid partner,
     * the string would look like this: AALAAPxxxxxxxxxx
     *
     */
    [this.UID2_BIT]: (sources: PrebidEids[]) =>
      this.getDefaultBitStatus(sources, "uidapi.com"),
  };

  private getDefaultBitStatus(
    prebidUserIds: PrebidEids[],
    sourceName: string,
  ): string {
    return prebidUserIds.find((obj) => obj.source === sourceName)
      ? this.PRESENT_PLACEHOLDER
      : this.ABSENT_PLACEHOLDER;
  }

  // HEM bit status is determined by the presence of HEM provided by LiveIntent (L) or MediaWiki (M).
  // In case if there is no HEM present, the bit is set to A.
  private getHEMBitStatus(): string {
    switch (true) {
      case this.checkIfLiveConnectSha2HEMIsPresent():
        // LiveIntent (L) - this is available when LiveIntent is on,
        // and their SHA2 HEM is stored in localStorage
        return this.LIVE_CONNECT_PLACEHOLDER;
      case !!getAdsContext()?.opts?.userEmailHashes?.length:
        // MediaWiki (M) - this is available when user is logged in on fandom wiki sites
        return this.MEDIA_WIKI_PLACEHOLDER;
      default:
        // Absent (A) [Does not exist]
        return this.ABSENT_PLACEHOLDER;
    }
  }

  /**
   * Sets initial targeting for LiveIntent module based on configuration.
   *
   * Targeting values:
   * - t0: LiveIntent module is disabled
   * - t1: LiveIntent module is enabled
   */
  private configureLiveIntentTargeting(adEngine: AdEngine): void {
    const isConnected = this.config.icLiveIntentConnectedId;
    const targeting = isConnected ? "t1" : "t0";
    adEngine.gptWrapper?.setTargeting("li-module-enabled", [targeting]);
    // Global variable needed because Amazon's apstag may override the GPT
    // targeting values before Odyssey targeting is applied.
    if (!isConnected) {
      window.liModuleEnabled = false;
      return;
    }
    window.liModuleEnabled = true;
  }

  /**
   * Updates LiveIntent targeting with enrichment status after IDs are fetched.
   * Only runs if LiveIntent is enabled.
   *
   * Targeting values:
   * - t1-e0: LiveIntent module enabled, but no ID was provided (failed enrichment)
   * - t1-e1: LiveIntent module enabled and ID was provided (successful enrichment)
   */
  private updateLiveIntentTargeting(adEngine: AdEngine): void {
    if (!this.config.icLiveIntentConnectedId) {
      return;
    }

    const isEnriched = this.getIds().some((eid) =>
      eid.source.includes("liveintent.com"),
    );
    adEngine.gptWrapper?.setTargeting("li-module-enabled", [
      `t1-${isEnriched ? "e1" : "e0"}`,
    ]);
  }

  getIds(): PrebidEids[] {
    return this.pbjs?.getUserIdsAsEids() ?? [];
  }

  generateBoiString(): string {
    const prebidUserIds = this.getIds();
    let idString = "";
    for (let i = 0; i < this.ID_STRING_LENGTH; i++) {
      const idProvider = this.ID_MAP[i];
      if (idProvider) {
        const bitStatus = idProvider(prebidUserIds);
        idString += bitStatus;
      } else {
        idString += this.BIT_PLACEHOLDER;
      }
    }

    return idString;
  }

  generateBoiAndSyncGptTargeting(adEngine: AdEngine) {
    const idString = this.generateBoiString();
    adEngine.gptWrapper.setTargeting("boi", idString);

    this.getIds().forEach((eid) => {
      if (eid.uids[0]?.id) {
        pushTrack(IdentityInfoEvent.IDENTITY_INFO, {
          partnerName: eid.source,
          partnerIdentityId: eid.uids[0].id,
        });
      }
    });
  }

  checkIfLiveConnectSha2HEMIsPresent(): boolean {
    try {
      // usePlugin throws an error if the passed in plugin in not loaded in.
      return this.usePlugin(LiveConnectPlugin)?.checkIfHemAvailable() ?? false;
    } catch {
      // If the LiveConnect plugin is not available, return false.
      // This could be because icLiveConnect variable was not set for
      // a specific Geo, or it was sampled through ICBM.
      // This case happens for Poland, where LiveConnect
      // is NOT enabled (at time of writing).
      return false;
    }
  }

  hooks: Hooks = {
    onBiddersInit: (adEngine: AdEngine) => {
      this.configureLiveIntentTargeting(adEngine);
      this.usePlugin(PrebidPlugin)?.addPrebidReadyCallback((pbjs: Prebid) => {
        this.pbjs = pbjs;
        this.pbjs.onEvent("requestBids", () => {
          // TODO: Maybe the results of this function should be cached after
          //  the first request goes out, and after X amount of seconds elapse?
          //  How often will the values from Prebid and the Identity partners change,
          //  after all the vendors are instantiated, and their initial requests are sent and settled?
          //  This could be a future optimization.
          //  NOTE - This needs to be discussed with the team.
          this.generateBoiAndSyncGptTargeting(adEngine);
          this.updateLiveIntentTargeting(adEngine);
        });
      });
    },
  };
}
